/**
 * Interlock authz policy — single source of truth for role → capability mapping.
 *
 * Security-critical: this file is the ONLY place the role capability matrix is
 * defined. Backend enforcement (packages/backend/src/authz/policy.ts +
 * requireCapability middleware) and frontend CTA gating both derive from this
 * matrix. Do not hardcode role comparisons for authz decisions anywhere else —
 * import `can()` (or the resolved capability set) instead.
 *
 * Roles (mirrors Prisma `UserRole` enum in packages/backend/prisma/schema.prisma):
 * - VIEWER: read-only.
 * - ENGINEER: engineering CRUD + trade participation + campaign scoring.
 * - MANAGER: arbitration + gate declaration + campaign running (no engineering CRUD).
 * - PROJECT_MANAGER: everything MANAGER + ENGINEER can do — "god mode WITH
 *   guardrails". Does NOT get ADMIN_OVERRIDE.
 * - ADMIN: everything, including ADMIN_OVERRIDE (force/bypass app rules, user mgmt).
 * - GUEST: deprecated alias of VIEWER, kept in the Prisma enum for backward
 *   compatibility with any existing rows; treated identically to VIEWER here.
 */
export const CAPABILITIES = [
    'READ',
    'ENGINEERING_WRITE',
    'TRADE_PARTICIPATE',
    'ARBITRATE',
    'GATE_DECLARE',
    'CAMPAIGN_RUN',
    'CAMPAIGN_SCORE',
    'ADMIN_OVERRIDE',
];
/**
 * THE capability matrix. Confirmed spec (do not deviate without a matching
 * PR to the spec doc + this file in the same change):
 *
 * | Capability          | Viewer | Engineer | Manager | ProjectManager | Admin |
 * |---------------------|--------|----------|---------|----------------|-------|
 * | READ                |   ✓    |    ✓     |    ✓    |       ✓        |   ✓   |
 * | ENGINEERING_WRITE    |   —    |    ✓     |    —    |       ✓        |   ✓   |
 * | TRADE_PARTICIPATE   |   —    |    ✓     |    ✓    |       ✓        |   ✓   |
 * | ARBITRATE           |   —    |    —     |    ✓    |       ✓        |   ✓   |
 * | GATE_DECLARE        |   —    |    —     |    ✓    |       ✓        |   ✓   |
 * | CAMPAIGN_RUN        |   —    |    —     |    ✓    |       ✓        |   ✓   |
 * | CAMPAIGN_SCORE      |   —    |    ✓     |    ✓    |       ✓        |   ✓   |
 * | ADMIN_OVERRIDE      |   —    |    —     |    —    |       —        |   ✓   |
 */
export const ROLE_CAPABILITIES = {
    VIEWER: new Set(['READ']),
    GUEST: new Set(['READ']), // deprecated alias of VIEWER
    ENGINEER: new Set([
        'READ',
        'ENGINEERING_WRITE',
        'TRADE_PARTICIPATE',
        'CAMPAIGN_SCORE',
    ]),
    MANAGER: new Set([
        'READ',
        'TRADE_PARTICIPATE',
        'ARBITRATE',
        'GATE_DECLARE',
        'CAMPAIGN_RUN',
        'CAMPAIGN_SCORE',
    ]),
    PROJECT_MANAGER: new Set([
        'READ',
        'ENGINEERING_WRITE',
        'TRADE_PARTICIPATE',
        'ARBITRATE',
        'GATE_DECLARE',
        'CAMPAIGN_RUN',
        'CAMPAIGN_SCORE',
        // Deliberately NOT ADMIN_OVERRIDE.
    ]),
    ADMIN: new Set([
        'READ',
        'ENGINEERING_WRITE',
        'TRADE_PARTICIPATE',
        'ARBITRATE',
        'GATE_DECLARE',
        'CAMPAIGN_RUN',
        'CAMPAIGN_SCORE',
        'ADMIN_OVERRIDE',
    ]),
};
/**
 * The single authz decision function. Everything else (backend middleware,
 * frontend CTA gating) is a thin wrapper around this.
 */
export function can(role, capability) {
    if (!role)
        return false;
    const caps = ROLE_CAPABILITIES[role];
    return caps ? caps.has(capability) : false;
}
/** Returns the full resolved capability set for a role (for `/api/me/capabilities`). */
export function capabilitiesForRole(role) {
    if (!role)
        return [];
    const caps = ROLE_CAPABILITIES[role];
    return caps ? Array.from(caps) : [];
}
/** All roles, in least-to-most-privileged-ish display order for role pickers. */
export const ALL_ROLES = [
    'VIEWER',
    'ENGINEER',
    'MANAGER',
    'PROJECT_MANAGER',
    'ADMIN',
];
export const ROLE_LABELS = {
    VIEWER: 'Viewer',
    GUEST: 'Guest (deprecated — treat as Viewer)',
    ENGINEER: 'Engineer',
    MANAGER: 'Manager',
    PROJECT_MANAGER: 'Project Manager',
    ADMIN: 'Admin',
};
//# sourceMappingURL=authz.js.map